/**
 * Lava — a tile-aligned hazard ZONE (Brief 012 Phase 1).
 *
 * "SPIKE generalised over a region": a `wCells × hCells` always-hurt rectangle. Unlike
 * SPIKE (one cell, costs a heart), lava defaults to INSTAKILL — touching it respawns
 * the player via ctx.killPlayer() (the same path as falling out of the world), so lava
 * reads lethal. Set `props.instakill: false` for a generic hazard FIELD that only costs
 * a heart (and so respects i-frames / star invuln).
 *
 * The "no new TileType" lock (Isaac) makes lava an ENTITY, not a liquid tile — it
 * serializes in level.entities[] like every other hazard.
 *
 * Determinism: surface animation is a frame-swap driven by an integer step counter — no
 * Math.sin, no wall-clock, no RNG. ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Fixed steps between the two lava surface frames (~0.3s @ 60Hz molten shimmer). */
const ANIM_STEPS = 18;

function numProp(props: Record<string, unknown> | undefined, key: string, fallback: number): number {
  const v = props?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

export class Lava implements Entity {
  readonly type = EntityType.LAVA;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly instakill: boolean;
  private readonly w: number;
  private readonly h: number;
  private frame = 0;

  /**
   * @param cx     placed-cell center-x (world px) — the region's TOP-LEFT cell.
   * @param feetY  placed-cell bottom (world px).
   * @param props  `wCells`/`hCells` (region size in tiles, def 3×1), `instakill`
   *               (default TRUE — lava is lethal; false = a heart-costing field).
   * @param ts     tile size (px).
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    const wCells = Math.max(1, Math.floor(numProp(props, 'wCells', 3)));
    const hCells = Math.max(1, Math.floor(numProp(props, 'hCells', 1)));
    this.w = wCells * ts;
    this.h = hCells * ts;
    // The placed cell is the region's top-left; grow right (wCells) and down (hCells).
    this.body = { x: cx - ts / 2, y: feetY - ts, w: this.w, h: this.h };
    this.instakill = props?.instakill !== false; // default lethal
  }

  update(_dt: number, _ctx: EntityContext): void {
    this.frame++;
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.instakill) ctx.killPlayer();   // respawn (fall-out-of-world path) — lava is lethal
    else ctx.hurtPlayer();                  // generic field: 1 heart, respects i-frames
  }

  spriteKey(): string {
    return Math.floor(this.frame / ANIM_STEPS) % 2 === 0 ? 'lava' : 'lava_alt';
  }
  visible(): boolean { return true; }
  /** Stretch one tile of lava art across the whole region (render-only). */
  renderSize(): { w: number; h: number } { return { w: this.w, h: this.h }; }
}
